import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Film, RotateCcw, ShieldCheck } from "lucide-react";

const STEPS = [
  "A test caller rings the RoofLead test number and the call goes unanswered",
  "The automated consent message plays",
  "The caller presses 1 to agree to texts",
  "The first text arrives from the approved testing sender",
  "The AI replies and qualifies the lead",
  "The lead appears in the dashboard as Qualified",
  "An inspection is booked manually in the dashboard and shows on the calendar",
];

export function DemoVideoPlayer() {
  const ref = useRef(null);
  const [meta, setMeta] = useState(null);
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let url;
    api.get("/demo/video/meta").then(async (r) => {
      setMeta(r.data);
      if (!r.data.available) return;
      const v = await api.get("/demo/video", { responseType: "blob" });
      url = URL.createObjectURL(v.data);
      setSrc(url);
    }).catch(() => setError(true));
    return () => url && URL.revokeObjectURL(url);
  }, []);

  const replay = () => { if (ref.current) { ref.current.currentTime = 0; ref.current.play(); } };

  return (
    <Card className="bg-slate-900 border-slate-800 overflow-hidden" data-testid="demo-video-card">
      <div className="aspect-video bg-black relative">
        {src ? (
          <video ref={ref} src={src} controls playsInline preload="metadata" className="w-full h-full" data-testid="demo-video-player" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-testid="demo-video-placeholder">
            <Film className="w-10 h-10 text-slate-600 mb-4" />
            <p className="font-mono text-xs uppercase tracking-widest text-amber-300">Placeholder</p>
            <p className="text-slate-300 mt-2 font-medium">
              {error ? "The recording couldn't be loaded. Please refresh." : meta ? "Recording coming soon" : "Loading…"}
            </p>
            <p className="text-slate-500 text-sm mt-1 max-w-md">The real internal test recording will appear here once it's uploaded. No video has been simulated.</p>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-slate-800">
        <p className="text-sm text-slate-300" data-testid="demo-video-title">{meta?.title || "RoofLead internal test: missed call to qualified lead"}</p>
        <Button size="sm" variant="outline" onClick={replay} disabled={!src} data-testid="demo-video-replay" className="border-slate-700 rounded-full">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Replay
        </Button>
      </div>
    </Card>
  );
}

export function DemoVideoNotes() {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="bg-slate-900 border-slate-800 p-6">
        <h2 className="font-display text-lg font-semibold text-white mb-4">What you'll see</h2>
        <ol className="space-y-2.5" data-testid="demo-video-steps">
          {STEPS.map((s, i) => (
            <li key={s} className="flex gap-3 text-sm text-slate-300">
              <span className="font-mono text-orange-400 w-5 shrink-0">{i + 1}.</span>{s}
            </li>
          ))}
        </ol>
      </Card>
      <Card className="bg-slate-900 border-slate-800 p-6" data-testid="demo-video-disclosure">
        <h2 className="font-display text-lg font-semibold text-white mb-4 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-400" /> About this recording</h2>
        <ul className="space-y-2.5 text-sm text-slate-300 list-disc pl-5">
          <li>Internal product demonstration recorded during RoofLead's authorized testing program.</li>
          <li>Test texts are sent by the approved testing sender, Brandon Rinker Jr., not by a roofing company.</li>
          <li>Phone numbers and personal details are hidden.</li>
          <li>The inspection is booked manually in the dashboard. Booking estimates by text is not shown.</li>
        </ul>
      </Card>
    </div>
  );
}
